import React, { useState, useEffect } from 'react';
import { db } from '../../lib/firebase';
import { doc, onSnapshot, query, collection, where } from 'firebase/firestore';
import { ConventionMetadata, ConShift, Prop, ConSignup, UserProfile } from '../../types';
import { format, parseISO } from 'date-fns';
import { safeFormatISO } from '../../lib/utils';
import { toZonedTime, format as formatTZ } from 'date-fns-tz';
import { Calendar, Clock, MapPin, Info, Users, Layout, Download } from 'lucide-react';
import { toast } from 'sonner';

const TIME_ZONE = 'America/Chicago';

interface ConPublicScheduleProps {
  conId: string;
  currentUserProfile?: UserProfile | null;
}

export default function ConPublicSchedule({ conId, currentUserProfile }: ConPublicScheduleProps) {
  const [convention, setConvention] = useState<ConventionMetadata | null>(null);
  const [shifts, setShifts] = useState<ConShift[]>([]);
  const [props, setProps] = useState<Prop[]>([]);
  const [signups, setSignups] = useState<ConSignup[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'schedule' | 'roster'>('schedule');
  const [isLightMode, setIsLightMode] = useState(false);
  const [useReadableFont, setUseReadableFont] = useState(true);

  // Helper to format in Central Time
  const formatInTZ = (dateISO: string, formatStr: string) => {
    try {
      const zonedDate = toZonedTime(parseISO(dateISO), TIME_ZONE);
      return formatTZ(zonedDate, formatStr, { timeZone: TIME_ZONE });
    } catch (e) {
      return 'Invalid Time';
    }
  };

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let hasLoadedCon = false;

    const unsubCon = onSnapshot(doc(db, 'conventions', conId), (snap) => {
      if (snap.exists()) {
        hasLoadedCon = true;
        setConvention({ id: snap.id, ...snap.data() } as ConventionMetadata);
        setNotFound(false);
        setLoading(false);
      } else if (!hasLoadedCon) {
        setNotFound(true);
        setLoading(false);
      }
    }, (error) => {
      console.error("Error loading convention:", error);
      setErrorMsg("Unable to load convention data. You may not have permission to view this schedule.");
      setLoading(false);
    });

    const unsubEvent = onSnapshot(doc(db, 'events', conId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.type === 'Convention') {
           setConvention(prev => {
             // If we already have a real convention doc, don't overwrite it
             if (prev && !prev.ownerId?.includes('command-deck')) return prev;
             
             hasLoadedCon = true;
             setNotFound(false);
             return {
               id: snap.id,
               name: data.name,
               startDate: data.startDate,
               endDate: data.endDate || data.startDate,
               ownerId: 'command-deck',
               status: 'active',
               dailyHours: data.dailyHours || {},
               address: data.location || ''
             } as ConventionMetadata;
           });
           setLoading(false);
         }
      }
    });

    const unsubProps = onSnapshot(collection(db, 'props'), (snap) => {
      setProps(snap.docs.map(d => ({ id: d.id, ...d.data() } as Prop)));
    }, (error) => {
      console.error("Error loading props:", error);
    });

    const qShifts = query(collection(db, 'shifts'), where('conId', '==', conId));
    const unsubShifts = onSnapshot(qShifts, (snap) => {
      setShifts(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConShift))
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
      );
    }, (error) => {
      console.error("Error loading shifts:", error);
    });

    const qSignups = query(collection(db, 'signups'), where('conId', '==', conId));
    const unsubSignups = onSnapshot(qSignups, (snap) => {
      setSignups(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConSignup)));
    }, (error) => {
      console.error("Error loading signups:", error);
    });

    const qRegs = query(collection(db, 'conRegistrations'), where('conId', '==', conId));
    const unsubRegs = onSnapshot(qRegs, (snap) => {
      setRegistrations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error("Error loading registrations:", error);
    });

    return () => {
      unsubCon();
      unsubEvent();
      unsubProps();
      unsubShifts();
      unsubSignups();
      unsubRegs();
    };
  }, [conId]);

  if (errorMsg) {
    return (
      <div className="py-24 text-center">
        <Clock className="w-12 h-12 mx-auto text-lcars-red mb-4" />
        <p className="font-bold uppercase tracking-widest text-lcars-red">SECURITY LOCKDOWN</p>
        <p className="text-sm mt-2 text-[var(--foreground)]/60">{errorMsg}</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="py-24 text-center">
        <Clock className="w-12 h-12 mx-auto text-lcars-red mb-4" />
        <p className="font-bold uppercase tracking-widest text-lcars-red">CONVENTION NOT FOUND</p>
        <p className="text-sm mt-2 text-[var(--foreground)]/60">This schedule link is invalid or the convention has been deleted.</p>
      </div>
    );
  }

  if (loading || !convention) {
    return (
      <div className="py-24 text-center">
        <Clock className="w-12 h-12 mx-auto animate-spin text-lcars-blue opacity-40 mb-4" />
        <p className="font-bold uppercase tracking-widest text-lcars-blue">Synchronizing Chronometers...</p>
      </div>
    );
  }

  const handleDownloadCSV = () => {
    if (!currentUserProfile) return;

    const isSysAdmin = currentUserProfile.roles?.includes('admin');
    const isUserMgr = currentUserProfile.roles?.includes('user_manager');
    const isAdmin = isSysAdmin || isUserMgr || currentUserProfile.roles?.includes('dept_lead');

    let csvContent = '';
    
    if (isAdmin) {
      // 1. Admin Download: all signups
      const headers = ['Shift Day', 'Shift Time', 'Station/Duty', 'Name', 'Ship', 'Pass Needed (y/n)', 'Contact Email', 'Contact Phone'];
      const rows = signups.map(signup => {
        const shift = shifts.find(s => s.id === signup.shiftId);
        const prop = props.find(p => p.id === shift?.propId);
        
        // Find matching registration
        const reg = registrations.find(r => 
          (signup.volunteerUid && r.uid === signup.volunteerUid) ||
          (signup.volunteerEmail && r.email?.toLowerCase() === signup.volunteerEmail.toLowerCase()) ||
          (signup.volunteerName && r.name?.toLowerCase() === signup.volunteerName.toLowerCase())
        );

        const contactEmail = signup.volunteerEmail || reg?.email || '';
        const contactPhone = reg?.phone || '';
        const ship = reg?.shipName || '';

        const shiftDay = shift?.startTime ? formatInTZ(shift.startTime, 'yyyy-MM-dd') : 'TBD';
        const shiftTime = shift?.startTime ? `${formatInTZ(shift.startTime, 'HH:mm')} - ${formatInTZ(shift.endTime, 'HH:mm')}` : 'TBD';

        return [
          shiftDay,
          shiftTime,
          prop?.name || 'General Duty',
          signup.volunteerName,
          ship,
          signup.needsBadge === 'yes' ? 'Y' : 'N',
          contactEmail,
          contactPhone
        ];
      });
      
      csvContent = [headers, ...rows].map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\n');
    } else {
      // 2. Member Download: only their own signups
      const headers = ['Shift Day', 'Shift Time', 'Station/Duty', 'Pass Needed (y/n)'];
      
      const memberSignups = signups.filter(s => 
        (s.volunteerUid === currentUserProfile.uid) || 
        (s.volunteerEmail && s.volunteerEmail.toLowerCase() === currentUserProfile.email.toLowerCase())
      );

      const rows = memberSignups.map(signup => {
        const shift = shifts.find(s => s.id === signup.shiftId);
        const prop = props.find(p => p.id === shift?.propId);
        
        const shiftDay = shift?.startTime ? formatInTZ(shift.startTime, 'yyyy-MM-dd') : 'TBD';
        const shiftTime = shift?.startTime ? `${formatInTZ(shift.startTime, 'HH:mm')} - ${formatInTZ(shift.endTime, 'HH:mm')}` : 'TBD';

        return [
          shiftDay,
          shiftTime,
          prop?.name || 'General Duty',
          signup.needsBadge === 'yes' ? 'Y' : 'N'
        ];
      });

      csvContent = [headers, ...rows].map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `schedule_${convention.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV download initiated.');
  };

  const groupedShifts = shifts.reduce((acc, shift) => {
    const date = formatInTZ(shift.startTime, 'yyyy-MM-dd');
    if (!acc[date]) acc[date] = [];
    acc[date].push(shift);
    return acc;
  }, {} as Record<string, ConShift[]>);

  const signupsByVolunteer = signups.reduce((acc, signup) => {
    if (!acc[signup.volunteerName]) acc[signup.volunteerName] = [];
    acc[signup.volunteerName].push(signup);
    return acc;
  }, {} as Record<string, ConSignup[]>);

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 print:py-0 print:px-0 print:max-w-none print:bg-white print:text-black">
      <div className="bg-lcars-blue text-black p-8 rounded-t-3xl border-b-8 border-black shadow-xl print:bg-white print:border-b-2 print:border-black print:rounded-none print:p-4 print:shadow-none print:text-black">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 print:flex-row">
          <div>
            <h1 className="text-4xl font-bold uppercase tracking-tighter mb-2">{convention.name}</h1>
            <div className="flex flex-wrap gap-4 text-sm font-bold opacity-80 uppercase tracking-widest">
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4"/> {convention.startDate && convention.endDate ? `${safeFormatISO(convention.startDate, 'MMMM dd')} - ${safeFormatISO(convention.endDate, 'MMMM dd, yyyy')}` : 'Dates TBD'}</span>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4"/> 
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(convention.address || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {convention.address || 'COORDINATES CLASSIFIED'}
                </a>
              </span>
            </div>
          </div>
          <div className="bg-[var(--background)]/20 p-4 rounded-xl border border-black/10 text-center min-w-[150px] print:border-black print:bg-transparent">
             <div className="text-[0.6rem] font-bold uppercase mb-1 print:text-black">Status</div>
             <div className="text-2xl font-black uppercase tracking-tighter print:text-black">OPERATIONAL</div>
          </div>
        </div>
      </div>

      <div className={`${
        isLightMode ? 'bg-slate-50 text-slate-900' : 'bg-white/5 text-[var(--foreground)]/80'
      } border-x-8 border-b-8 border-lcars-blue p-8 rounded-b-[40px] space-y-12 shadow-2xl backdrop-blur-sm print:bg-white print:border-none print:p-4 print:shadow-none print:space-y-6`}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 print:block">
          <div className="lg:col-span-1 space-y-6 print:mb-8 print:break-inside-avoid">
             <div className="bg-lcars-tan text-black p-4 rounded-tr-[30px] font-bold uppercase tracking-widest text-xs print:bg-transparent print:border-b-2 print:border-black print:p-2 print:text-black print:rounded-none">
               Operational Parameters
             </div>
             <div className={`space-y-4 text-sm font-bold uppercase tracking-widest ${isLightMode ? 'text-slate-800' : 'text-[var(--foreground)]/80'} print:text-black`}>
                <div className={`flex justify-between items-center py-2 border-b ${isLightMode ? 'border-black/20' : 'border-white/20'} print:border-black/20`}>
                  <span className="print:text-black">Manning Depth</span>
                  <span className={`${isLightMode ? 'text-black' : 'text-[var(--foreground)]'} print:text-black`}>{convention.volunteersPerShift} PERS / STN</span>
                </div>
                <div className={`flex justify-between items-center py-2 border-b ${isLightMode ? 'border-black/20' : 'border-white/20'} print:border-black/20`}>
                  <span className="print:text-black">Total Stations</span>
                  <span className={`${isLightMode ? 'text-black' : 'text-[var(--foreground)]'} print:text-black`}>{convention.sessionIds?.length || 0}</span>
                </div>
                <div className="py-2">
                   <p className={`text-xs ${isLightMode ? 'text-slate-600' : 'text-[var(--foreground)]/70'} italic normal-case leading-relaxed print:text-black`}>
                     The following schedule represents the confirmed operational periods for Starfleet recruitment stations. All times are strictly **Central Time (CT)**.
                   </p>
                </div>
             </div>
          </div>

          {convention.dailyHours && Object.keys(convention.dailyHours).length > 0 && (
            <div className="space-y-4 mb-8 print:break-inside-avoid">
               <div className="bg-lcars-blue text-black p-4 rounded-tr-[30px] font-bold uppercase tracking-widest text-xs print:bg-transparent print:border-b-2 print:border-black print:p-2 print:text-black print:rounded-none">
                 Opening Frequencies
               </div>
               <div className="space-y-2">
                 {Object.entries(convention.dailyHours as Record<string, { open: string; close: string }>)
                   .sort(([a], [b]) => a.localeCompare(b))
                   .map(([date, hours]) => (
                     <div key={date} className={`flex justify-between items-center py-2 border-b ${isLightMode ? 'border-black/20' : 'border-white/10'} print:border-black/20 text-sm lg:text-[0.7rem] font-bold uppercase tracking-widest leading-relaxed px-2 ${isLightMode ? 'text-slate-800' : 'text-[var(--foreground)]'} print:text-black`}>
                       <span className="text-lcars-tan print:text-black">{date ? safeFormatISO(`${date}T12:00:00`, 'EEE MMM dd') : 'TBD'}</span>
                       <span className={`${isLightMode ? 'text-black' : 'text-[var(--foreground)]'} print:text-black tabular-nums`}>{hours.open && hours.close ? `${hours.open} - ${hours.close}` : 'CLOCK HOURS PENDING'}</span>
                     </div>
                   ))}
               </div>
            </div>
          )}

          <div className="lg:col-span-3 space-y-12 print:space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4 print:hidden">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setIsLightMode(!isLightMode)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase transition-all border ${
                    isLightMode
                      ? 'bg-[var(--background)] text-[var(--foreground)] border-black'
                      : 'bg-white/10 text-[var(--foreground)] border-white/20 hover:bg-white/20'
                  }`}
                >
                  {isLightMode ? '☀️ Dark Mode' : '🌙 Light Background'}
                </button>
                <button
                  onClick={() => setUseReadableFont(!useReadableFont)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase transition-all border ${
                    useReadableFont
                      ? 'bg-[var(--background)] text-[var(--foreground)] border-black'
                      : 'bg-white/10 text-[var(--foreground)] border-white/20 hover:bg-white/20'
                  }`}
                >
                  {useReadableFont ? '🔤 LCARS Font' : '🔤 Standard Font'}
                </button>
                {currentUserProfile && (
                  <button
                    onClick={handleDownloadCSV}
                    className="px-4 py-1.5 rounded-full text-xs font-bold uppercase transition-all border bg-lcars-gold text-black border-lcars-gold hover:bg-lcars-gold/80 flex items-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" /> Download CSV
                  </button>
                )}
              </div>

              <div className="bg-[var(--background)]/40 rounded-full p-1 flex">
                <button 
                  onClick={() => setViewMode('schedule')}
                  className={`px-6 py-2 rounded-full text-xs font-bold uppercase transition-all ${viewMode === 'schedule' ? 'bg-lcars-blue text-black' : 'text-lcars-blue hover:bg-white/5'}`}
                >
                  By Shift
                </button>
                <button 
                  onClick={() => setViewMode('roster')}
                  className={`px-6 py-2 rounded-full text-xs font-bold uppercase transition-all ${viewMode === 'roster' ? 'bg-lcars-tan text-black' : 'text-lcars-tan hover:bg-white/5'}`}
                >
                  Personnel Roster
                </button>
              </div>
            </div>

            {viewMode === 'schedule' && (
              <>
                {Object.entries(groupedShifts).map(([date, dateShifts]: [string, any[]]) => (
                  <div key={date} className="space-y-6 print:space-y-4 print:mb-8 print:break-inside-avoid">
                    <div className="flex items-center gap-4 print:border-b-2 print:border-black print:pb-2">
                      <div className="h-10 w-3 bg-lcars-gold rounded-full print:hidden"></div>
                      <h2 className="text-2xl font-bold text-lcars-gold uppercase tracking-tighter print:text-black">
                        {date && date !== 'undefined' ? safeFormatISO(`${date}T12:00:00`, 'EEEE, MMMM dd') : 'Date TBD'}
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:ml-6 print:ml-0 print:grid-cols-2 print:gap-4">
                      {dateShifts.map(shift => {
                        const prop = props.find(p => p.id === shift.propId);
                        const shiftSignups = signups.filter(s => s.shiftId === shift.id);
                        const count = shiftSignups.length;
                        const max = shift.maxVolunteers || prop?.staffNeeded || 0;

                        return (
                          <div key={shift.id} className={`${
                            isLightMode 
                              ? 'bg-white border-slate-200 shadow-md hover:bg-slate-50' 
                              : 'bg-white/5 border-white/20 hover:bg-white/10'
                          } border rounded-[30px] p-6 lcars-corner-tr relative overflow-hidden group transition-all print:bg-white print:border print:border-black print:rounded-none print:p-4 print:break-inside-avoid`}>
                            <div className="absolute top-0 right-0 w-16 h-16 bg-lcars-blue/5 rounded-bl-[30px] print:hidden" />
                            
                            <div className="flex justify-between items-center text-lcars-blue mb-2 print:text-black">
                               <h3 className="font-bold text-lg uppercase tracking-tight">{prop?.name || 'Unknown'}</h3>
                               <span className="text-xs uppercase font-bold">{count} / {max} FILLED</span>
                            </div>

                            <div className="flex items-center gap-3 mb-4 print:text-black">
                                <Clock className="w-5 h-5 text-lcars-blue print:text-black" />
                                <span className={`text-xl font-bold tracking-widest print:text-black ${
                                  isLightMode ? 'text-slate-800' : 'text-[var(--foreground)]'
                                }`}>
                                  {formatInTZ(shift.startTime, 'HH:mm')} - {formatInTZ(shift.endTime, 'HH:mm')}
                                </span>
                            </div>
                            
                            {shiftSignups.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {shiftSignups.map((signup) => (
                                  <span 
                                    key={signup.id} 
                                    className={`bg-lcars-blue/20 text-lcars-blue border border-lcars-blue/40 px-3 py-1 rounded-[10px] text-xs font-bold uppercase tracking-wider print:bg-transparent print:text-black print:border-black ${
                                      useReadableFont ? 'font-readability font-bold text-sm px-4 py-1.5 shadow-sm' : ''
                                    }`}
                                  >
                                    {signup.volunteerName}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="mt-4 print:hidden">
                               <div className={`flex items-center gap-2 text-[0.6rem] font-bold uppercase mb-2 ${
                                 isLightMode ? 'text-slate-500' : 'text-[var(--foreground)]/60'
                               }`}>
                                  <Users className="w-3 h-3" /> Manning Intensity
                               </div>
                               <div className="flex items-center gap-4">
                                  <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-lcars-blue" 
                                      style={{ width: `${Math.min(100, (count / max) * 100)}%` }}
                                    ></div>
                                  </div>
                                  <span className={`text-[0.7rem] font-bold whitespace-nowrap ${
                                    isLightMode ? 'text-slate-700' : 'text-[var(--foreground)]'
                                  }`}>
                                    {count} / {max} PERSONNEL
                                  </span>
                               </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {shifts.length === 0 && (
                  <div className="py-24 text-center">
                    <Info className="w-16 h-16 mx-auto mb-4 text-[var(--foreground)]/20" />
                    <p className="text-xl font-bold text-[var(--foreground)]/40 uppercase tracking-widest">No shift cycles defined for this stardate.</p>
                  </div>
                )}
              </>
            )}

            {viewMode === 'roster' && (
              <div className="space-y-6 print:space-y-4">
                <div className="flex items-center gap-4 mb-8 print:border-b-2 print:border-black print:pb-2 print:mb-4">
                  <div className="h-10 w-3 bg-lcars-tan rounded-full print:hidden"></div>
                  <h2 className={`text-2xl font-bold uppercase tracking-tighter print:text-black ${
                    isLightMode ? 'text-slate-800' : 'text-lcars-tan'
                  }`}>
                    Personnel Manifest
                  </h2>
                </div>
                
                {Object.keys(signupsByVolunteer).length === 0 ? (
                  <div className="py-24 text-center">
                    <Users className="w-16 h-16 mx-auto mb-4 text-[var(--foreground)]/20" />
                    <p className="text-xl font-bold text-[var(--foreground)]/40 uppercase tracking-widest">No personnel assigned to this operation.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:ml-6 print:ml-0 print:grid-cols-2">
                    {Object.entries(signupsByVolunteer)
                      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
                      .map(([volunteerName, volunteerSignups]: [string, ConSignup[]]) => (
                        <div key={volunteerName} className={`${
                          isLightMode 
                            ? 'bg-white border-slate-200 shadow-md hover:bg-slate-50' 
                            : 'bg-white/5 border-white/20 hover:bg-white/10'
                        } border rounded-[30px] p-6 lcars-corner-tr relative group transition-all print:bg-white print:border print:border-black print:rounded-none print:p-4 print:break-inside-avoid`}>
                          <div className="absolute top-0 right-0 w-16 h-16 bg-lcars-tan/5 rounded-bl-[30px] print:hidden" />
                          
                          <div className={`flex items-center gap-3 mb-4 border-b pb-4 ${
                            isLightMode ? 'border-slate-200' : 'border-white/20'
                          } print:border-black/20`}>
                            <Users className="w-5 h-5 text-lcars-tan print:text-black" />
                            <h3 className={`font-bold text-xl uppercase tracking-wider print:text-black ${
                              isLightMode ? 'text-slate-800' : 'text-[var(--foreground)]'
                            } ${
                              useReadableFont ? 'font-readability font-black text-2xl normal-case' : ''
                            }`}>{volunteerName}</h3>
                          </div>
                          
                          <div className="space-y-3">
                            {volunteerSignups
                              .sort((a, b) => {
                                const shiftA = shifts.find(s => s.id === a.shiftId);
                                const shiftB = shifts.find(s => s.id === b.shiftId);
                                if (!shiftA || !shiftB) return 0;
                                return shiftA.startTime.localeCompare(shiftB.startTime);
                              })
                              .map(signup => {
                                const shift = shifts.find(s => s.id === signup.shiftId);
                                if (!shift) return null;
                                const prop = props.find(p => p.id === shift.propId);
                                
                                return (
                                  <div key={signup.id} className={`${
                                    isLightMode ? 'bg-slate-50 border-slate-200' : 'bg-[var(--background)]/40 border-white/10'
                                  } rounded-xl p-3 flex justify-between items-center border print:bg-transparent print:border-none print:p-1 print:border-b print:border-black/10 print:rounded-none`}>
                                    <div>
                                      <div className="text-xs font-bold text-lcars-tan uppercase print:text-black">{prop?.name || 'Unknown Duty'}</div>
                                      <div className={`text-[0.65rem] font-bold uppercase mt-1 print:text-black ${
                                        isLightMode ? 'text-slate-600' : 'text-[var(--foreground)]/80'
                                      }`}>
                                        {formatInTZ(shift.startTime, 'EEE MMM dd')}
                                      </div>
                                    </div>
                                    <div className="text-sm font-bold text-lcars-blue tabular-nums print:text-black">
                                      {formatInTZ(shift.startTime, 'HH:mm')} - {formatInTZ(shift.endTime, 'HH:mm')}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
